import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { formatApiError } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/verify`);
          if (response.data.success) {
            setUser(response.data.data.user);
          } else {
            // Token is invalid
            localStorage.removeItem('token');
            setToken(null);
          }
        } catch (error) {
          console.error('Auth verification failed:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      if (response.data.success) {
        const { access_token, user: userData } = response.data.data;
        
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        
        return { success: true };
      } else {
        return { success: false, error: 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: formatApiError(error, 'Login failed') 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);

      if (response.data.success) {
        const { access_token, user: newUser } = response.data.data;
        
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(newUser);
        
        return { success: true };
      } else {
        return { success: false, error: 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: formatApiError(error, 'Registration failed') 
      };
    }
  };

  const applySession = (access_token, userData) => {
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
  };

  // Exchanges the one-time code from the Google callback (URL fragment) for a real session. Admin status is decided server-side.
  const loginWithGoogleCode = async (code) => {
    try {
      const response = await axios.post(`${API}/auth/google/exchange`, { code });
      const data = response.data.data;
      if (data.kind === 'admin') {
        localStorage.setItem('adminToken', data.admin_token);
        localStorage.setItem('adminInfo', JSON.stringify(data.admin_info));
        if (data.user_session) applySession(data.user_session.access_token, data.user_session.user);
        return { success: true, kind: 'admin' };
      }
      if (data.kind === 'user') {
        applySession(data.access_token, data.user);
        return { success: true, kind: 'user' };
      }
      return { success: true, kind: 'onboarding', onboarding: { token: data.onboarding_token, email: data.email, name: data.name } };
    } catch (error) {
      return { success: false, error: formatApiError(error, 'Google sign-in failed') };
    }
  };

  const completeGoogleProfile = async (payload) => {
    try {
      const response = await axios.post(`${API}/auth/google/complete`, payload);
      applySession(response.data.data.access_token, response.data.data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiError(error, 'Could not complete sign-up') };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API}/users/me`, profileData);
      
      if (response.data.success) {
        setUser(prevUser => ({ ...prevUser, ...response.data.data }));
        return { success: true };
      } else {
        return { success: false, error: 'Profile update failed' };
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return { 
        success: false, 
        error: formatApiError(error, 'Profile update failed') 
      };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify`);
      if (response.data.success) setUser(response.data.data.user);
      return response.data.data.user;
    } catch {
      return null;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    loginWithGoogleCode,
    completeGoogleProfile,
    logout,
    updateProfile,
    refreshUser,
    isAuthenticated: !!token && !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};